import React from "react";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useChatSidebar } from "@/contexts/ChatSidebarContext";
import { FeedbackButton } from "./FeedbackButton";

interface DashboardLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  header: React.ReactNode;
  rightSidebar?: React.ReactNode;
}

// Custom Sidebar wrapper to handle full collapse
const CollapsibleSidebar: React.FC<{ content: React.ReactNode }> = ({
  content,
}) => {
  const { state } = useSidebar();
  const isExpanded = state === "expanded";

  return (
    <aside
      data-sidebar="sidebar"
      className={`
        min-h-screen
        transition-all duration-200
        bg-sidebar text-sidebar-foreground
        flex flex-col
        ${isExpanded ? "w-64 min-w-[16rem]" : "w-12 min-w-[3rem]"}
        border-r border-sidebar-border
        shadow-md
        z-[110]
      `}
    >
      {/* Logo and Brand section with collapse button */}
      <div className="flex items-center border-b border-sidebar-border h-12">
        {isExpanded ? (
          // Expanded view with logo, title and collapse button
          <>
            <div className="flex items-center flex-1 pl-3">
              <img
                src={`${process.env.PUBLIC_URL}/ivy-favicon.png`}
                alt="Ivy Logo"
                className="w-8 h-8"
              />
              <span className="ml-2 font-bold text-base text-gray-800 dark:text-gray-200">
                Ivy
              </span>
            </div>
            <div className="pr-2">
              <SidebarTrigger className="text-sidebar-foreground hover:bg-sidebar-accent" />
            </div>
          </>
        ) : (
          // Collapsed view with just logo in center
          <div className="flex items-center justify-center w-full">
            <img
              src={`${process.env.PUBLIC_URL}/ivy-favicon.png`}
              alt="Ivy Logo"
              className="w-6 h-6"
            />
          </div>
        )}
      </div>

      {/* Collapse button when sidebar is collapsed */}
      {!isExpanded && (
        <div className="flex justify-center py-2 border-b border-sidebar-border">
          <SidebarTrigger className="text-sidebar-foreground hover:bg-sidebar-accent" />
        </div>
      )}

      {/* Sidebar content - always visible but styled differently based on state */}
      <div className="flex-1 overflow-auto flex flex-col">
        <div className="flex-1">{content}</div>
        <FeedbackButton />
      </div>
    </aside>
  );
};

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  sidebar,
  header,
  rightSidebar,
}) => {
  const { isOpen: chatSidebarOpen, width: chatSidebarWidth } = useChatSidebar();

  return (
    <div className="flex min-h-screen w-full">
      <SidebarProvider defaultOpen={false}>
        {/* Sidebar on the left, full height */}
        <CollapsibleSidebar content={sidebar} />

        {/* Main area: header + content in a column */}
        <div
          id="dashboard-main-content"
          className="relative flex flex-col flex-1 min-w-0 transition-all duration-300 ease-out overflow-hidden"
          style={{
            marginRight: chatSidebarOpen ? `${chatSidebarWidth}px` : "0px",
            width: chatSidebarOpen
              ? `calc(100vw - ${chatSidebarWidth}px)`
              : "100vw",
            height: "100vh", // Explicit height to contain scrolling
          }}
        >
          {/* Header with modern grey styling */}
          <div className="bg-gray-100 dark:bg-gray-800 shadow-sm relative z-20 flex-shrink-0">
            {header}
          </div>
          {/* Scrollable main content area */}
          <div className="flex-1 overflow-auto">
            <main className="relative p-6 md:p-10 bg-gray-50 dark:bg-gray-900">
              {children}
            </main>
          </div>
        </div>

        {/* Right Sidebar */}
        {rightSidebar}
      </SidebarProvider>
    </div>
  );
};

export default DashboardLayout;
